import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication.service';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';
import { CustomValidators } from 'src/app/core/validators/custom-validators';

@Component({
    selector: 'app-password-confirmation-dialog',
    templateUrl: './password-confirmation-dialog.component.html',
    styleUrls: ['./password-confirmation-dialog.component.scss']
})
export class PasswordConfirmationDialogComponent implements OnDestroy {
    readonly form = new FormGroup({
        password: new FormControl('', {
            nonNullable: true,
            validators: CustomValidators.required(
                CustomValidators.translate('common.password-confirmation.password-required', 'La contraseña no puede estar vacía.')
            ),
        }),
    });
    verifying = false;
    error = '';
    private readonly destroyed$ = new Subject<void>();

    constructor(
        private readonly authService: AuthenticationService,
        private readonly dialogRef: MatDialogRef<PasswordConfirmationDialogComponent, boolean>,
        private readonly i18n: ExperienceI18nService,
    ) {}

    confirm(): void {
        if (this.verifying || this.form.invalid) return;
        this.verifying = true;
        this.error = '';
        this.dialogRef.disableClose = true;
        this.authService.verifyPassword(this.form.controls.password.value).pipe(
            takeUntil(this.destroyed$),
            finalize(() => {
                this.verifying = false;
                this.dialogRef.disableClose = false;
                this.form.reset();
            }),
        ).subscribe({
            next: () => {
                this.form.reset();
                this.dialogRef.close(true);
            },
            error: (response: HttpErrorResponse) => {
            const code = response.error?.error?.message ?? response.error?.code ?? response.error?.message;
                if (response.status === 403 && code === 'INVALID_PASSWORD') {
                    this.error = this.i18n.translate('common.password-confirmation.invalid-password', undefined, 'La contraseña es incorrecta.',);
                } else if (response.status === 401) {
                    this.error = this.i18n.translate('common.password-confirmation.session-expired', undefined, 'La sesión venció. Inicie sesión nuevamente.');
                } else if (response.status === 429) {
                    this.error = this.i18n.translate('common.password-confirmation.too-many-attempts', undefined, 'Demasiados intentos. Intente nuevamente más tarde.');
                } else if (response.status === 403) {
                    this.error = this.i18n.translate('common.password-confirmation.rejected', undefined, 'No se pudo confirmar su identidad o no tiene permiso.');
                } else {
                    this.error = this.i18n.translate('common.password-confirmation.unavailable', undefined, 'No se pudo verificar la contraseña. Intente nuevamente.');
                }
            },
        });
    }

    cancel(): void {
        if (this.verifying) return;
        this.form.reset();
        this.dialogRef.close(false);
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.form.reset();
    }
}
